import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * Reinicio del entorno de pruebas. **Solo TEST.**
 *
 * Deja TEST como recién desplegado para empezar una ronda de pruebas limpia:
 * reinicia el estado de todas las conversaciones —incluidas las derivadas a un
 * asesor, que quedan sin asignación ni reloj de SLA—, borra los documentos y
 * las alertas, libera los códigos de cliente y manda a la papelera de Drive las
 * carpetas que creó el bot.
 *
 * **Conserva el historial del chat y los archivos en S3.** Los mensajes son lo
 * que el asesor lee y el material con el que se le enseña al agente; y con los
 * archivos en S3 se puede reprocesar todo sin volver a pedirle nada al cliente.
 *
 * La pantalla pide el plan primero (el backend no escribe nada sin la palabra
 * de confirmación) para que se vea qué va a pasar antes de decidir. El corte
 * por ambiente está en el servidor: fuera de TEST la ruta responde 404.
 */

interface Conversacion {
  id: string
  estado: string
  asignada_a?: string
  campos: string[]
  timers: string[]
}

interface Reporte {
  ambiente: string
  aplicado: boolean
  conversaciones: Conversacion[]
  documentos: number
  alertas: number
  codigos: number
  drive: { carpetas: string[]; errores: string[] }
  conserva: string[]
}

const PALABRA = 'REINICIAR'

export default function ReiniciarPruebas() {
  const [plan, setPlan] = useState<Reporte | null>(null)
  const [hecho, setHecho] = useState<Reporte | null>(null)
  const [cargando, setCargando] = useState(true)
  const [aplicando, setAplicando] = useState(false)
  const [error, setError] = useState('')
  const [palabra, setPalabra] = useState('')

  const pedirPlan = useCallback(async () => {
    setCargando(true)
    setError('')
    try {
      const { data } = await apiClient.post<Reporte>('/test-reset', {})
      setPlan(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el estado')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void pedirPlan()
  }, [pedirPlan])

  async function reiniciar() {
    setAplicando(true)
    setError('')
    try {
      const { data } = await apiClient.post<Reporte>('/test-reset', {
        confirmacion: PALABRA,
      })
      setHecho(data)
      setPalabra('')
      await pedirPlan()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reiniciar')
    } finally {
      setAplicando(false)
    }
  }

  if (cargando && !plan) return <LoadingSpinner />

  const conSuciedad = plan
    ? plan.conversaciones.filter((c) => c.campos.length || c.timers.length)
    : []
  const derivadas = plan
    ? plan.conversaciones.filter((c) => c.asignada_a).length
    : 0

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {error && <ErrorAlert message={error} />}

      {hecho && (
        <p className="rounded-card border border-brand-border bg-brand-light px-4 py-3 text-body font-semibold text-brand-hover">
          Entorno reiniciado: {hecho.conversaciones.length} conversación(es),{' '}
          {hecho.documentos} documento(s), {hecho.alertas} alerta(s) y{' '}
          {hecho.drive.carpetas.length} carpeta(s) de Drive a la papelera.
        </p>
      )}

      <section className="rounded-card border border-brand-border bg-white p-5">
        <h2 className="text-h3 font-semibold">Reiniciar el entorno de pruebas</h2>
        <p className="mt-2 text-body text-brand-muted">
          Deja TEST como recién desplegado para empezar una ronda limpia. Las
          conversaciones derivadas a un asesor también se liberan.
        </p>

        {plan && (
          <dl className="mt-4 grid grid-cols-2 gap-3 text-body sm:grid-cols-4">
            <Dato n={conSuciedad.length} de={plan.conversaciones.length} label="conversaciones" />
            <Dato n={plan.documentos} label="documentos" />
            <Dato n={plan.alertas} label="alertas" />
            <Dato n={plan.drive.carpetas.length} label="carpetas de Drive" />
          </dl>
        )}

        {derivadas > 0 && (
          <p className="mt-3 text-body text-brand-muted">
            {derivadas} está{derivadas === 1 ? '' : 'n'} asignada
            {derivadas === 1 ? '' : 's'} a un asesor: se libera
            {derivadas === 1 ? '' : 'n'} la asignación y el reloj de SLA.
          </p>
        )}

        <div className="mt-4 rounded-card bg-brand-light px-4 py-3 text-body">
          <b>Se conserva:</b> el historial del chat y los archivos en S3. Las
          carpetas de Drive van a la papelera, que las retiene 30 días.
        </div>

        <label className="mt-4 block text-body font-semibold" htmlFor="confirmar">
          Escribí {PALABRA} para confirmar
        </label>
        <div className="mt-1 flex gap-2">
          <input
            id="confirmar"
            className="w-48 rounded-card border border-brand-border px-3 py-2 text-body"
            value={palabra}
            onChange={(e) => setPalabra(e.target.value)}
            placeholder={PALABRA}
            autoComplete="off"
          />
          <button
            type="button"
            className="rounded-card bg-brand px-4 py-2 text-body font-semibold text-white disabled:opacity-40"
            disabled={palabra.trim().toUpperCase() !== PALABRA || aplicando}
            onClick={() => void reiniciar()}
          >
            {aplicando ? 'Reiniciando…' : 'Reiniciar entorno'}
          </button>
        </div>
      </section>

      {plan && conSuciedad.length > 0 && (
        <section className="rounded-card border border-brand-border bg-white p-5">
          <h3 className="text-body font-semibold">Qué se va a limpiar</h3>
          <ul className="mt-2 flex flex-col gap-1 text-body text-brand-muted">
            {conSuciedad.map((c) => (
              <li key={c.id}>
                <span className="font-mono">{c.id.slice(0, 8)}</span> · {c.estado}
                {c.asignada_a ? ` · asignada a ${c.asignada_a}` : ''} ·{' '}
                {c.campos.length} campo(s)
                {c.timers.length ? ` · ${c.timers.length} timer(s)` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {plan && plan.drive.errores.length > 0 && (
        <ErrorAlert
          message={`Drive: ${plan.drive.errores.join(' · ')}`}
        />
      )}
    </div>
  )
}

function Dato({ n, de, label }: { n: number; de?: number; label: string }) {
  return (
    <div className="rounded-card bg-brand-light px-3 py-2">
      <div className="text-h3 font-semibold">
        {n}
        {de !== undefined && <span className="text-body text-brand-muted"> / {de}</span>}
      </div>
      <div className="text-caption text-brand-muted">{label}</div>
    </div>
  )
}
